import OpenInNew from "@mui/icons-material/OpenInNew";
import {
  GridLegacy as Grid,
  Paper,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { DataGrid } from "components/DataGrid";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  CohortMemberStatus,
  GetTierIiReportQuery,
} from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useCallback, useMemo } from "react";
import { getCohortDisplayStatus } from "util/cohortStatus";
import { CohortStatusChip } from "./CohortStatusChip";

type Row = GetTierIiReportQuery["tierIIReport"]["cohortMemberships"][0];

export function CohortSummary({
  cohortMemberships,
}: {
  cohortMemberships?: GetTierIiReportQuery["tierIIReport"]["cohortMemberships"];
}) {
  const alerts = useAlerts();
  const theme = useTheme();

  const viewCohort = useCallback(
    (id: string) => {
      const newTab = window.open(
        `${window.location.origin}/staff/cohorts/${id}`,
        "_blank"
      );
      if (newTab) {
        newTab.focus();
      } else {
        alerts.error(
          "Unable to open the URL in a new tab. Please allow pop-ups for this website."
        );
      }
    },
    [alerts]
  );

  const columns = useMemo((): OmnisearchGridColDef<Row>[] => {
    return [
      {
        field: "type",
        headerName: "Cohort Type",
        flex: 2,
        valueGetter: (_, row) => prettyPrintEnumValue(row.cohort.type),
      },
      {
        field: "status",
        headerName: "Cohort Status",
        flex: 1,
        renderCell: ({ row }) => (
          <CohortStatusChip status={getCohortDisplayStatus(row.cohort)} />
        ),
      },
      {
        field: "totalCount",
        headerName: "Total",
        flex: 1,
        valueGetter: (_, row) => row.cohort.members.length,
      },
      {
        field: "readyCount",
        headerName: "Ready",
        flex: 1,
        valueGetter: (_, row) =>
          getMemberStatusCount(row, CohortMemberStatus.Ready),
      },
      {
        field: "notReadyCount",
        headerName: "Not Ready",
        flex: 1,
        valueGetter: (_, row) =>
          getMemberStatusCount(row, CohortMemberStatus.NotReady),
      },
      {
        field: "notParticipatingCount",
        headerName: "Not Participating",
        flex: 1,
        valueGetter: (_, row) =>
          getMemberStatusCount(row, CohortMemberStatus.NotParticipating),
      },
      {
        field: "unknownCount",
        headerName: "Unknown",
        flex: 1,
        valueGetter: (_, row) =>
          getMemberStatusCount(row, CohortMemberStatus.Unknown),
      },
      {
        field: "moreInfo",
        type: "actions",
        getActions: (params) => [
          <Tooltip title="View Cohort" key={params.row.cohort.id}>
            <GridActionsCellItem
              onClick={() => viewCohort(params.row.cohort.id)}
              label="View Cohort"
              icon={<OpenInNew />}
            />
          </Tooltip>,
        ],
      },
    ];
  }, [viewCohort]);

  if (!cohortMemberships || !cohortMemberships.length) return null;

  return (
    <Grid
      container
      component={Paper}
      elevation={0}
      flexDirection="column"
      sx={{
        padding: theme.spacing(3),
      }}
    >
      <Typography variant="h6" sx={{ mb: 2 }}>
        Cohorts
      </Typography>
      <div
        style={{
          width: "100%",
          overflow: "auto",
        }}
      >
        <DataGrid
          rows={cohortMemberships}
          columns={columns}
          disableRowSelectionOnClick
          onRowClick={({ row }: { row: Row }) => viewCohort(row.cohort.id)}
          autoHeight
          sx={{
            minWidth: 800, // Minimum width to maintain column visibility
            width: "100%",
            height: "auto",
            "& .MuiDataGrid-root": {
              minWidth: 800,
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: theme.palette.grey[50],
            },
            "& .MuiDataGrid-columnHeader": {
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
            "& .MuiDataGrid-cell": {
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
            // Responsive behavior for small screens
            [theme.breakpoints.down("md")]: {
              "& .MuiDataGrid-columnHeader": {
                fontSize: "0.75rem",
              },
              "& .MuiDataGrid-cell": {
                fontSize: "0.75rem",
              },
            },
          }}
        />
      </div>
    </Grid>
  );
}

const getMemberStatusCount = (row: Row, status: CohortMemberStatus) =>
  row.cohort.members.filter((m) => m.status === status).length;
